// client/src/pages/CheckoutPage.js
import React, { useContext, useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Stack, Card, CardContent, Button, Divider, Alert, Grid, TextField, MenuItem,
  ToggleButtonGroup, ToggleButton, Stepper, Step, StepLabel, StepButton, CircularProgress, Chip, FormControlLabel, Checkbox,
} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import PaymentIcon from '@mui/icons-material/Payment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import { formatCurrency } from '../utils/currency';
import { getImageUrl, API_BASE_URL } from '../utils/media';
import { PAYMENT_METHODS } from '../constants/paymentMethods';
import { FREE_SHIPPING_THRESHOLD, ESTIMATED_DELIVERY_DAYS, calculateShippingFee } from '../constants/shipping';

const VAT_RATE = 0.15;
const RAND_PER_POINT = 0.1;
const PAYMENT_ICONS = { CreditCard: CreditCardIcon, Apple: AppleIcon, Google: GoogleIcon, Payment: PaymentIcon };
const STEPS = ['Your Account', 'Delivery Method', 'Payment Method', 'Review & Place Order'];

const emptyAddress = { name: '', addressLine1: '', addressLine2: '', city: '', province: '', postalCode: '', country: 'South Africa', phone: '' };
const CARD_SX = { borderRadius: 4, '&:hover': { transform: 'none', boxShadow: 'none' } };

const CheckoutPage = () => {
  const { cartItems, clearCart, getCartTotal } = useContext(CartContext);
  const { token, user, userProfile } = useContext(AuthContext);
  const { isEnabled } = useContext(FeatureFlagContext);
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [checkingOutAsGuest, setCheckingOutAsGuest] = useState(false);
  const [guestInfo, setGuestInfo] = useState({ name: '', email: '' });
  const [fulfillmentType, setFulfillmentType] = useState('delivery');
  const [address, setAddress] = useState(emptyAddress);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('new');
  const [saveAddress, setSaveAddress] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [pointsToRedeem, setPointsToRedeem] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');

  const isGuest = !user && checkingOutAsGuest;

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        setSavedAddresses(data);
        const def = data.find((a) => a.is_default) || data[0];
        if (def) {
          setSelectedAddressId(def.id);
          setAddress({
            name: def.name, addressLine1: def.address_line1, addressLine2: def.address_line2 || '',
            city: def.city, province: def.province || '', postalCode: def.postal_code,
            country: def.country, phone: def.phone || '',
          });
        }
      })
      .catch(() => {});
  }, [token]);

  if (cartItems.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info">Your cart is empty. Add something before checking out.</Alert>
        <Button component={RouterLink} to="/" sx={{ mt: 2 }}>Continue Shopping</Button>
      </Container>
    );
  }

  const subtotal = getCartTotal();
  const couponDiscount = coupon ? coupon.discountAmount : 0;
  const maxRedeemablePoints = user ? Math.min(userProfile?.loyalty_points || 0, Math.floor(subtotal / RAND_PER_POINT)) : 0;
  const pointsDiscount = Math.min(Number(pointsToRedeem) || 0, maxRedeemablePoints) * RAND_PER_POINT;
  const discount = couponDiscount + pointsDiscount;
  const shippingFee = fulfillmentType === 'collection' ? 0 : calculateShippingFee(subtotal);
  // Prices are VAT-inclusive - the VAT line shows the portion already in the
  // total (mirrors the order route on the server).
  const total = Math.max(subtotal - discount + shippingFee, 0);
  const vat = Math.round((total * VAT_RATE / (1 + VAT_RATE)) * 100) / 100;

  const handleAddressChange = (e) => setAddress({ ...address, [e.target.name]: e.target.value });

  const handleSelectSavedAddress = (id) => {
    setSelectedAddressId(id);
    if (id === 'new') { setAddress(emptyAddress); return; }
    const found = savedAddresses.find((a) => String(a.id) === String(id));
    if (found) {
      setAddress({
        name: found.name, addressLine1: found.address_line1, addressLine2: found.address_line2 || '',
        city: found.city, province: found.province || '', postalCode: found.postal_code,
        country: found.country, phone: found.phone || '',
      });
    }
  };

  const handleApplyCoupon = async () => {
    setCouponError('');
    setCouponLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: couponInput.trim().toUpperCase(), subtotal }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Invalid coupon.');
      setCoupon(data);
    } catch (err) {
      setCoupon(null);
      setCouponError(err.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    setPlacing(true);
    setError('');

    const orderItems = cartItems.map((item) => ({
      productId: item.id,
      quantity: item.quantity,
      variantId: item.variantId || undefined,
    }));

    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      if (saveAddress && token && selectedAddressId === 'new') {
        await fetch(`${API_BASE_URL}/api/addresses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            name: address.name, addressLine1: address.addressLine1, addressLine2: address.addressLine2,
            city: address.city, province: address.province, postalCode: address.postalCode,
            country: address.country, phone: address.phone, isDefault: savedAddresses.length === 0,
          }),
        }).catch(() => {});
      }

      const orderResponse = await fetch(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          items: orderItems,
          shippingAddress: address,
          couponCode: coupon?.code,
          fulfillmentType,
          paymentMethod,
          pointsToRedeem: Number(pointsToRedeem) || 0,
          guestName: isGuest ? guestInfo.name : undefined,
          guestEmail: isGuest ? guestInfo.email : undefined,
        }),
      });
      const orderData = await orderResponse.json();
      if (!orderResponse.ok) throw new Error(orderData.error || 'Failed to place order.');

      const paymentResponse = await fetch(`${API_BASE_URL}/api/payments/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ orderId: orderData.orderId, method: paymentMethod }),
      });
      const paymentData = await paymentResponse.json();
      if (!paymentResponse.ok) throw new Error(paymentData.error || 'Failed to start payment.');

      clearCart();
      navigate(paymentData.redirectPath, { state: { orderId: orderData.orderId } });
    } catch (err) {
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  const deliveryValid = fulfillmentType === 'delivery'
    ? Boolean(address.name && address.addressLine1 && address.city && address.postalCode)
    : Boolean(address.name && address.phone);
  const guestValid = Boolean(guestInfo.name && guestInfo.email);

  const stepMax = (user || isGuest) ? 3 : 0; // Can't move past the account step until signed in or continuing as guest.

  const goToStep = (step) => {
    if (step <= stepMax) setActiveStep(step);
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 6 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.14em', fontWeight: 700 }}>Secure checkout</Typography>
      <Typography variant="h3" fontWeight={800} letterSpacing="-0.02em" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 3 }}>Checkout</Typography>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>}

      <Stepper activeStep={activeStep} sx={{ mb: 4, flexWrap: { xs: 'wrap', sm: 'nowrap' }, '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: 'primary.main' } }} alternativeLabel>
        {STEPS.map((label, index) => (
          <Step key={label} completed={index < stepMax || (index === 0 && Boolean(user || isGuest))}>
            <StepButton onClick={() => goToStep(index)} disabled={index > stepMax}>
              <StepLabel>{label}</StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          {activeStep === 0 && (
            <Card sx={CARD_SX}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Your Account</Typography>
                {!user && !checkingOutAsGuest ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>You need to be signed in to check out{isEnabled('guest_checkout') ? ', or continue as a guest' : ''}.</Alert>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Button variant="contained" component={RouterLink} to="/login?redirect=/checkout">Login</Button>
                      <Button variant="outlined" component={RouterLink} to="/register?redirect=/checkout">Create Account</Button>
                      {isEnabled('guest_checkout') && (
                        <Button variant="text" onClick={() => setCheckingOutAsGuest(true)}>Continue as Guest</Button>
                      )}
                    </Stack>
                  </>
                ) : isGuest ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>Checking out as a guest — no account needed.</Alert>
                    <Stack spacing={2} sx={{ maxWidth: 420 }}>
                      <TextField label="Full Name" value={guestInfo.name} onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })} required fullWidth size="small" />
                      <TextField label="Email" type="email" value={guestInfo.email} onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })} required fullWidth size="small" />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                      <Button variant="contained" disabled={!guestValid} onClick={() => setActiveStep(1)}>Continue</Button>
                      <Button variant="text" onClick={() => setCheckingOutAsGuest(false)}>Actually, I'll sign in</Button>
                    </Stack>
                  </>
                ) : !userProfile ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Signed in as <strong>{userProfile.email}</strong>
                    </Alert>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" component="span">Your client number for this order:</Typography>
                      <Chip label={userProfile.client_number} size="small" color="primary" variant="outlined" />
                    </Box>
                    {userProfile.loyalty_points > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        You have <strong>{userProfile.loyalty_points} loyalty points</strong> ({formatCurrency(userProfile.loyalty_points * RAND_PER_POINT)}) available to redeem later in checkout.
                      </Typography>
                    )}
                    <Button variant="contained" onClick={() => setActiveStep(1)} sx={{ mt: userProfile.loyalty_points > 0 ? 0 : 3 }}>Continue</Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {activeStep === 1 && (
            <Card sx={CARD_SX}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Delivery Method</Typography>
                <ToggleButtonGroup
                  value={fulfillmentType}
                  exclusive
                  onChange={(e, val) => val && setFulfillmentType(val)}
                  sx={{ mb: 3, '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: 'divider !important', borderRadius: '8px !important' } }}
                >
                  <ToggleButton value="delivery" sx={{ px: 3, textTransform: 'none' }}>
                    <LocalShippingOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Delivery
                  </ToggleButton>
                  <ToggleButton value="collection" sx={{ px: 3, textTransform: 'none' }}>
                    <StorefrontOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Collection
                  </ToggleButton>
                </ToggleButtonGroup>

                {fulfillmentType === 'delivery' && savedAddresses.length > 0 && (
                  <TextField
                    select label="Address" value={selectedAddressId} onChange={(e) => handleSelectSavedAddress(e.target.value)}
                    fullWidth size="small" sx={{ mb: 2 }}
                  >
                    {savedAddresses.map((a) => (
                      <MenuItem key={a.id} value={a.id}>{a.label} — {a.name}, {a.address_line1}, {a.city}</MenuItem>
                    ))}
                    <MenuItem value="new">+ Enter a new address</MenuItem>
                  </TextField>
                )}

                {fulfillmentType === 'delivery' ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Full Name" name="name" value={address.name} onChange={handleAddressChange} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Phone" name="phone" value={address.phone} onChange={handleAddressChange} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Address Line 1" name="addressLine1" value={address.addressLine1} onChange={handleAddressChange} required fullWidth size="small" autoComplete="address-line1" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Address Line 2 (optional)" name="addressLine2" value={address.addressLine2} onChange={handleAddressChange} fullWidth size="small" autoComplete="address-line2" />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField label="City" name="city" value={address.city} onChange={handleAddressChange} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Province" name="province" value={address.province} onChange={handleAddressChange} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField label="Postal Code" name="postalCode" value={address.postalCode} onChange={handleAddressChange} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select label="Country" name="country" value={address.country} onChange={handleAddressChange} fullWidth size="small">
                        <MenuItem value="South Africa">South Africa</MenuItem>
                        <MenuItem value="Namibia">Namibia</MenuItem>
                        <MenuItem value="Botswana">Botswana</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </TextField>
                    </Grid>
                    {user && selectedAddressId === 'new' && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={<Checkbox checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)} />}
                          label="Save this address to my account"
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocalShippingOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Estimated delivery: {ESTIMATED_DELIVERY_DAYS}
                          {shippingFee === 0 ? ' · Free shipping applied' : ` · Free shipping over ${formatCurrency(FREE_SHIPPING_THRESHOLD)}`}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        No shipping fee. We'll contact you to arrange a time once your order is ready to collect.
                      </Alert>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Full Name" name="name" value={address.name} onChange={handleAddressChange} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Phone" name="phone" value={address.phone} onChange={handleAddressChange} required fullWidth size="small" />
                    </Grid>
                  </Grid>
                )}

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button onClick={() => setActiveStep(0)}>Back</Button>
                  <Button variant="contained" disabled={!deliveryValid} onClick={() => setActiveStep(2)}>Continue</Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {activeStep === 2 && (
            <Card sx={CARD_SX}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Payment Method</Typography>
                <ToggleButtonGroup
                  value={paymentMethod}
                  exclusive
                  onChange={(e, val) => val && setPaymentMethod(val)}
                  sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: 'divider !important', borderRadius: '8px !important' } }}
                >
                  {PAYMENT_METHODS.map((m) => {
                    const Icon = PAYMENT_ICONS[m.icon];
                    return (
                      <ToggleButton key={m.value} value={m.value} sx={{ px: 2, textTransform: 'none' }}>
                        <Icon fontSize="small" sx={{ mr: 1 }} />
                        {m.label}
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button onClick={() => setActiveStep(1)}>Back</Button>
                  <Button variant="contained" onClick={() => setActiveStep(3)}>Continue</Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {activeStep === 3 && (
            <Stack spacing={2}>
              <Card sx={CARD_SX}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>Review Your Order</Typography>
                  <Stack spacing={1.5}>
                    {cartItems.map((item) => (
                      <Stack key={item.cartKey} direction="row" alignItems="center" spacing={2}>
                        <Box component="img" src={getImageUrl(item.imageUrl)} alt={item.name} sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1 }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                          {item.variantLabel && <Typography variant="caption" color="text.secondary">{item.variantLabel} · </Typography>}
                          <Typography variant="caption" color="text.secondary">Qty {item.quantity}</Typography>
                        </Box>
                        <Typography variant="body2">{formatCurrency(item.price * item.quantity)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    {fulfillmentType === 'collection' ? <StorefrontOutlinedIcon fontSize="small" /> : <LocalShippingOutlinedIcon fontSize="small" />}
                    <Typography variant="body2">
                      {fulfillmentType === 'collection' ? 'Collection' : 'Delivery'} — {address.name}
                      {fulfillmentType === 'delivery' && `, ${address.addressLine1}, ${address.city}`}
                      {address.phone && ` (${address.phone})`}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Payment method: {PAYMENT_METHODS.find((m) => m.value === paymentMethod)?.label}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={CARD_SX}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Have a coupon?</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small" fullWidth placeholder="Coupon code" value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      disabled={Boolean(coupon)}
                    />
                    {coupon ? (
                      <Button variant="outlined" color="error" onClick={() => { setCoupon(null); setCouponInput(''); }}>Remove</Button>
                    ) : (
                      <Button variant="outlined" onClick={handleApplyCoupon} disabled={!couponInput.trim() || couponLoading}>Apply</Button>
                    )}
                  </Stack>
                  {couponError && <Alert severity="error" sx={{ mt: 1 }}>{couponError}</Alert>}
                  {coupon && <Alert severity="success" sx={{ mt: 1 }}>Coupon "{coupon.code}" applied.</Alert>}
                </CardContent>
              </Card>

              {maxRedeemablePoints > 0 && (
                <Card sx={CARD_SX}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Redeem loyalty points?</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      You have {userProfile.loyalty_points} points available ({formatCurrency(userProfile.loyalty_points * RAND_PER_POINT)}).
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small" type="number" label="Points to redeem" value={pointsToRedeem}
                        onChange={(e) => setPointsToRedeem(e.target.value)}
                        inputProps={{ min: 0, max: maxRedeemablePoints }}
                        sx={{ width: 160 }}
                      />
                      <Button size="small" onClick={() => setPointsToRedeem(String(maxRedeemablePoints))}>Use Max</Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              <Stack direction="row" spacing={2}>
                <Button onClick={() => setActiveStep(2)}>Back</Button>
              </Stack>
            </Stack>
          )}
        </Grid>

        <Grid item xs={12} md={5} sx={{ position: { md: 'sticky' }, top: { md: 96 }, alignSelf: 'flex-start' }}>
          <Card sx={CARD_SX}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h6" fontWeight={800} gutterBottom>Order summary</Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography>{formatCurrency(subtotal)}</Typography>
                </Stack>
                {couponDiscount > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="success.main">Coupon Discount</Typography>
                    <Typography color="success.main">-{formatCurrency(couponDiscount)}</Typography>
                  </Stack>
                )}
                {pointsDiscount > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="success.main">Points Redeemed</Typography>
                    <Typography color="success.main">-{formatCurrency(pointsDiscount)}</Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Shipping</Typography>
                  <Typography>{shippingFee === 0 ? 'Free' : formatCurrency(shippingFee)}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={700}>Total</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(total)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Includes VAT (15%)</Typography>
                  <Typography variant="body2" color="text.secondary">{formatCurrency(vat)}</Typography>
                </Stack>
              </Stack>

              {activeStep === 3 && (
                <Button
                  fullWidth variant="contained" size="large" sx={{ mt: 3 }}
                  onClick={handlePlaceOrder}
                  disabled={placing || !deliveryValid}
                >
                  {placing ? 'Placing Order...' : 'Place Order'}
                </Button>
              )}
              <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  256-bit SSL encrypted &amp; secure checkout
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;
